import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";
import { categoryVisualMap } from "./categoryVisualMap";
import { compressImage } from "../../utils/imageCompress";
import "./CreateSearch.scss";
import { useToast } from "../Alerts/Toast";
import DynamicHeader from "../Header/DynamicHeader";
import { useNavigate } from "react-router-dom";
import SuccessModal from "./SuccessModal";
import addImageIcon from "./img/add-image.png";
import DraftResumePrompt from "./DraftResumePrompt";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

/* ===== Draft (localStorage) ===== */
const SEARCH_DRAFT_LS_KEY = "cvanto:draft:createSearch:v1";

const safeJsonParse = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const nowIso = () => new Date().toISOString();

const CreateSearch = ({ embedded = false, animate = false }) => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photoObj, setPhotoObj] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [showCategories, setShowCategories] = useState(false);
  const [mounted, setMounted] = useState(false);

  const selectRef = useRef(null);
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const [showSuccess, setShowSuccess] = useState(false);

  /* ===== Draft prompt state ===== */
  const [draftCandidate, setDraftCandidate] = useState(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const draftHydratedRef = useRef(false);
  const suppressDraftWriteRef = useRef(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showCategories) return;
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowCategories(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") setShowCategories(false);
    };

    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showCategories]);

  const triggerFileDialog = () => fileInputRef.current?.click();

  const uploadSingleFile = async (file, onProgress) => {
    const compressed = await compressImage(file, {
      maxSide: 1600,
      mimeType: "image/webp",
      quality: 0.82,
    });

    const fd = new FormData();
    fd.append("photo", compressed, compressed.name);

    const res = await axios.post(`${API_BASE}/api/listings/upload-photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!evt.total) return;
        const pct = Math.round((evt.loaded * 100) / evt.total);
        onProgress?.(pct);
      },
    });

    return res.data.photoUrl;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tmpUrl = URL.createObjectURL(file);
    setPhotoObj({ url: tmpUrl, uploading: true, progress: 0, isLocal: true });

    try {
      setUploading(true);

      const uploadedUrl = await uploadSingleFile(file, (pct) => {
        setPhotoObj((prev) => (prev ? { ...prev, progress: pct } : prev));
      });

      setPhotoObj({
        url: uploadedUrl,
        uploading: false,
        progress: 100,
        isLocal: false,
      });
    } catch (err) {
      console.error("❌ Помилка завантаження фото", err);
      toast.error("Помилка завантаження фото", 3200);
      setPhotoObj((prev) =>
        prev ? { ...prev, uploading: false, error: true } : prev
      );
    } finally {
      setUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const handleRemovePhoto = () => setPhotoObj(null);

  const handleSelectCategory = (name) => {
    setCategory(name);
    setShowCategories(false);
  };

  const categories = Object.keys(categoryVisualMap);

  const scrollToAndMark = useCallback((ref, className = "field-error") => {
    const node = ref?.current ?? ref;
    if (!node) return;

    node.classList.add(className);
    node.scrollIntoView?.({ behavior: "smooth", block: "center" });

    const inputEl = node.querySelector?.("input, textarea, select") || node;
    inputEl.focus?.();

    setTimeout(() => node.classList.remove(className), 2400);
  }, []);

  /* ===== Draft: build / save / load / clear ===== */
  const buildSearchDraft = useCallback(() => {
    const uploadedPhoto =
      photoObj && !photoObj.uploading && !photoObj.error && !photoObj.isLocal
        ? photoObj.url
        : null;

    return {
      version: 1,
      kind: "search",
      updatedAt: nowIso(),
      title: title || "",
      description: description || "",
      category: category || "",
      location: location || "",
      photo: uploadedPhoto,
      hasPhoto: !!uploadedPhoto,
    };
  }, [title, description, category, location, photoObj]);

  const draftLooksMeaningful = useCallback((d) => {
    if (!d) return false;
    const t = (d.title || "").trim();
    const desc = (d.description || "").trim();
    const hasPhoto = !!d.photo;
    const hasCat = !!(d.category || "");
    const hasLoc = !!(d.location || "").trim();
    return !!(t || desc || hasPhoto || hasCat || hasLoc);
  }, []);

  const clearSearchDraft = useCallback(() => {
    try {
      localStorage.removeItem(SEARCH_DRAFT_LS_KEY);
    } catch {}
  }, []);

  const hydrateFromDraft = useCallback((d) => {
    if (!d) return;

    suppressDraftWriteRef.current = true;

    setTitle(d.title || "");
    setDescription(d.description || "");
    setCategory(d.category || "");
    setLocation(d.location || "");
    setShowCategories(false);

    if (d.photo) {
      setPhotoObj({
        url: d.photo,
        uploading: false,
        progress: 100,
        isLocal: false,
      });
    } else {
      setPhotoObj(null);
    }

    requestAnimationFrame(() => {
      suppressDraftWriteRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (draftHydratedRef.current) return;
    draftHydratedRef.current = true;

    try {
      const raw = localStorage.getItem(SEARCH_DRAFT_LS_KEY);
      const parsed = safeJsonParse(raw || "");
      if (parsed && parsed.kind === "search" && draftLooksMeaningful(parsed)) {
        setDraftCandidate(parsed);
        setShowDraftPrompt(true);
      }
    } catch {}
  }, [draftLooksMeaningful]);

  const draftWriteTimerRef = useRef(null);

  useEffect(() => {
    if (suppressDraftWriteRef.current) return;

    const draft = buildSearchDraft();

    if (!draftLooksMeaningful(draft)) {
      clearTimeout(draftWriteTimerRef.current);
      draftWriteTimerRef.current = setTimeout(() => {
        clearSearchDraft();
      }, 450);
      return;
    }

    clearTimeout(draftWriteTimerRef.current);
    draftWriteTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(SEARCH_DRAFT_LS_KEY, JSON.stringify(draft));
      } catch {}
    }, 450);

    return () => clearTimeout(draftWriteTimerRef.current);
  }, [buildSearchDraft, draftLooksMeaningful, clearSearchDraft]);

  const resetSearchForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setPhotoObj(null);
    setCategory("");
    setLocation("");
    setShowCategories(false);
    setMessage("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      toast.error("Ви не авторизовані", 3200);
      return;
    }

    if (!title.trim()) {
      setMessage("🔎 Напишіть, що саме ви шукаєте");
      scrollToAndMark(titleRef);
      return;
    }

    if (!category) {
      setMessage("🗂️ Оберіть категорію, щоб запит легше знаходився");
      if (selectRef.current) scrollToAndMark(selectRef);
      return;
    }

    const payload = {
      userId,
      title: title.trim(),
      description: description.trim(),
      category: category || "",
      photos: photoObj?.url ? [photoObj.url] : [],
      price: 0,
      type: "wanted",
      currency: "",
      exchangeFor: "",
      location: location.trim(),
      attributes: {},
      showPhone: false,
      contactPhone: null,
      condition: "na",
      isNegotiable: false,
    };

        try {
      const token = await getAccessToken();

      if (!token) {
        toast.error("Сесія недійсна. Увійдіть повторно.", 3200);
        return;
      }

      const res = await axios.post(`${API_BASE}/api/listings`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Search created:", res.data);

      toast.success("Запит опубліковано", 2200);
      setShowSuccess(true);

      clearSearchDraft();
      resetSearchForm();
    } catch (err) {
      console.error("Помилка створення запиту", err?.response?.data || err);
      toast.error(
        err?.response?.data?.error || "Не вдалося опублікувати запит",
        3200
      );
    }
  };

  return (
    <>
      <DraftResumePrompt
        open={showDraftPrompt}
        title="Незавершений запит"
        subtitle="Ви вже починали створювати запит. Продовжимо чи почнемо заново?"
        meta=""
        onResume={() => {
          hydrateFromDraft(draftCandidate);
          setShowDraftPrompt(false);
          setDraftCandidate(null);
          toast.success("Чернетку відновлено", 2000);
        }}
        onDiscard={() => {
          clearSearchDraft();
          setShowDraftPrompt(false);
          setDraftCandidate(null);
          resetSearchForm();
          toast.success("Чернетку очищено", 2000);
        }}
        onClose={() => setShowDraftPrompt(false)}
      />

      {!embedded && (
        <>
          <DynamicHeader
            title="Створити запит"
            variant="glass"
            size="compact"
            hideOnScroll={true}
            showBack={true}
            showMenu={false}
          />
          <div className="local-header-spacer small" />
        </>
      )}

      <div
        className={`create-search ios-glass-card ${
          mounted ? "enter" : ""
        } ${animate ? "swap-enter" : ""} ${embedded ? "embedded" : ""} ${
          showCategories ? "dropdown-open" : ""
        }`}
        role="region"
        aria-label="Форма створення запиту"
      >
        <h2>Створити запит</h2>
        {message && <p className="message">{message}</p>}

        <form onSubmit={handleSubmit} className="create-search-form" noValidate>
          <input
            ref={titleRef}
            type="text"
            className="pill-50"
            placeholder="Що ви шукаєте? *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <textarea
            className="pill-50"
            placeholder="Опишіть детальніше, що саме вам потрібно"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="custom-select-wrapper" ref={selectRef}>
            <div
              className={`custom-select-trigger pill-50 ${
                showCategories ? "open" : ""
              }`}
              onClick={() => setShowCategories((s) => !s)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") &&
                setShowCategories((s) => !s)
              }
              aria-haspopup="listbox"
              aria-expanded={showCategories}
              aria-controls="search-category-dropdown"
            >
              {category ? (
                <div className="selected-category">
                  <div
                    className="category-icon"
                    style={{
                      backgroundImage: `url(${categoryVisualMap[category]})`,
                    }}
                  />
                  <span>{category}</span>
                </div>
              ) : (
                <span className="placeholder">Категорія *</span>
              )}
              <span className="chevron" aria-hidden="true">
                ▾
              </span>
            </div>

            <div
              id="search-category-dropdown"
              className={`custom-select-dropdown ${
                showCategories ? "open" : "closed"
              }`}
              role="listbox"
              aria-hidden={!showCategories}
            >
              {categories.map((name, idx) => (
                <div
                  key={name}
                  className="dropdown-item"
                  style={{ "--item-index": idx }}
                  onClick={() => handleSelectCategory(name)}
                  role="option"
                  aria-selected={category === name}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleSelectCategory(name);
                    }
                  }}
                >
                  <div
                    className="category-icon"
                    style={{
                      backgroundImage: `url(${categoryVisualMap[name]})`,
                    }}
                  />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          <input
            type="text"
            className="pill-50"
            placeholder="Локація (необов'язково)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />

          <div className="photo-upload">
            <label>Фото (необов'язково)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: "none" }}
              disabled={uploading}
            />

            {!photoObj && (
              <button
                type="button"
                className="photo-tile empty add-one"
                onClick={triggerFileDialog}
                disabled={uploading}
                aria-label="Додати фото"
                title="Додати фото"
              >
                <img
                  src={addImageIcon}
                  alt="Додати фото"
                  className="add-image-icon"
                />
              </button>
            )}
          </div>

          {photoObj && (
            <div className="photo-grid single">
              <div
                className={`tile filled ${
                  photoObj.uploading ? "is-uploading" : ""
                }`}
              >
                <img src={photoObj.url} alt="Фото запиту" />
                {!photoObj.uploading && <span className="main-badge">Фото</span>}

                {photoObj.uploading && (
                  <div
                    className="progress-overlay"
                    aria-label="Завантаження фото"
                  >
                    <div className="ring">
                      <svg
                        viewBox="0 0 36 36"
                        width="64"
                        height="64"
                        aria-hidden="true"
                      >
                        <path
                          className="bg"
                          d="M18 2.0845
                             a 15.9155 15.9155 0 0 1 0 31.831
                             a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        <path
                          className="meter"
                          strokeDasharray={`${Math.max(
                            1,
                            Math.min(100, photoObj.progress ?? 0)
                          )}, 100`}
                          d="M18 2.0845
                             a 15.9155 15.9155 0 0 1 0 31.831
                             a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <div className="pct">{photoObj.progress ?? 0}%</div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="remove-btn"
                  onClick={handleRemovePhoto}
                  aria-label="Видалити фото"
                  title="Видалити фото"
                  disabled={photoObj.uploading}
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="cta-sticky">
            <button
              type="submit"
              disabled={uploading}
              className="primary-cta with-sheen"
            >
              {uploading ? "Завантаження..." : "Опублікувати запит"}
            </button>
          </div>

          <div className="bottom-safe-space" aria-hidden="true" />
        </form>
      </div>

      <SuccessModal
        open={showSuccess}
        title="Опубліковано"
        subtitle="Запит додано у стрічку"
        onComplete={() => navigate("/feed")}
      />
    </>
  );
};

export default CreateSearch;
